import { useState } from 'react';
import { confirmPhoneVerification, startPhoneVerification } from '../../services/tenantApi';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import { formatPhoneInputLive } from '../../utils/phone-display';

const fieldClass =
  'w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400';

const buttonClass =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-white/5';

/**
 * Confirm a replacement phone with a Twilio code without leaving this profile.
 * The confirmed number becomes primary. The previous number stays on the person.
 */
export function ChangeVerifiedPhone({ onChanged }: { onChanged: () => Promise<void> }) {
  const getToken = useTenantApiToken();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setOpen(false);
    setPhone('');
    setCode('');
    setCodeSent(false);
    setError(null);
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await startPhoneVerification(getToken, phone);
      setCodeSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not text that number.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await confirmPhoneVerification(getToken, phone, code);
      await onChanged();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code is wrong or expired.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className={`${buttonClass} mt-2`} onClick={() => setOpen(true)}>
        Change number
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        We&apos;ll text a code to the new number. You stay on this profile, and your current number stays on file.
      </p>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="New phone number"
        aria-label="New phone number"
        value={phone}
        onChange={(event) => {
          setPhone(formatPhoneInputLive(event.target.value));
          setCodeSent(false);
          setCode('');
        }}
        className={fieldClass}
      />
      {codeSent ? (
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="6-digit code"
          aria-label="Verification code"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          className={fieldClass}
        />
      ) : null}
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        {codeSent ? (
          <button type="button" className={buttonClass} disabled={busy || code.length < 4} onClick={() => void confirm()}>
            {busy ? 'Checking…' : 'Confirm number'}
          </button>
        ) : (
          <button type="button" className={buttonClass} disabled={busy || phone.replace(/\D/g, '').length < 10} onClick={() => void sendCode()}>
            {busy ? 'Texting…' : 'Text me a code'}
          </button>
        )}
        {codeSent ? (
          <button type="button" className={buttonClass} disabled={busy} onClick={() => void sendCode()}>
            Text a new code
          </button>
        ) : null}
        <button type="button" className={buttonClass} disabled={busy} onClick={reset}>
          Cancel
        </button>
      </div>
    </div>
  );
}
